/**
 * A single named quantity within a report chart: one bar, one donut slice,
 * one ranked row. Shared across the chart primitives so a view-configuration
 * driven report can hand the same aggregate shape to whichever chart type a
 * card asks for, instead of each chart inventing its own point shape.
 */
export interface ChartSegment {
  readonly label: string;
  readonly value: number;
  /**
   * Marks a segment as the "not recorded" bucket: it renders in a neutral
   * grey instead of the chart palette, so an unknown value never reads as if
   * it were a real outcome. Segments render in the order given; ordering the
   * bucket last is the caller's choice.
   */
  readonly muted?: boolean;
}

/** One point on a time-series (e.g. daily) trend chart. */
export interface ChartTimeSeriesPoint {
  /** ISO date (`YYYY-MM-DD`) or other x-axis label. */
  readonly label: string;
  readonly value: number;
}

/**
 * Chart palette, in order: pulls from the `--chart-1..8` design tokens so
 * chart colours stay in sync with the rest of the design system (including
 * dark mode) instead of hard-coded hex values. Eight entries rather than
 * five: `chartColorFor` wraps with a modulo, and a five-entry palette made a
 * six-segment breakdown (e.g. dossier type) repeat a colour.
 */
export const chartPalette = [
  "var(--color-chart-1)",
  "var(--color-chart-2)",
  "var(--color-chart-3)",
  "var(--color-chart-4)",
  "var(--color-chart-5)",
  "var(--color-chart-6)",
  "var(--color-chart-7)",
  "var(--color-chart-8)",
] as const;

/** Neutral colour for {@link ChartSegment.muted} segments. */
export const chartMutedColor = "var(--color-muted-foreground)";

/** Picks a palette colour for a segment, respecting `muted`. */
export function chartColorFor(segment: ChartSegment, index: number): string {
  return segment.muted
    ? chartMutedColor
    : chartPalette[index % chartPalette.length];
}
